import { useSelector } from 'react-redux';
import {
    selectedAlaCarteBenefits,
    selectModularAddOnBenefits,
    selectModularAlaCarteBenefits,
    selectModularPlans,
    selectModularSelectedAddOns,
    selectModularSelectedBasePlan,
    selectModularSelectedUserBenefits,
    selectModularWalletBalance
} from '../../../../redux/slices/ModularEnrolmentSlice/selectors';
import { useMemo } from 'react';
import { BenefitPricingStructure, IAlacarte, IPriceSummary } from '../../../../redux/slices/PolicyListSlice/types';
import { IUsePremiumBreakdown } from '../types';

const usePremiumBreakdown = (): IUsePremiumBreakdown => {
    const selectedModularPlan = useSelector(selectModularSelectedBasePlan);
    const modularPlansData = useSelector(selectModularPlans);
    const selectedAlaCarte = useSelector(selectedAlaCarteBenefits);
    const selectedAddOns = useSelector(selectModularSelectedAddOns);
    const alacarteBenefitsData = useSelector(selectModularAlaCarteBenefits);
    const addOnBenefitsData = useSelector(selectModularAddOnBenefits);
    const userBenefits = useSelector(selectModularSelectedUserBenefits);
    const balance = useSelector(selectModularWalletBalance);
    const modularPlans = modularPlansData.data || [];
    const alacarteBenefits = alacarteBenefitsData.data || [];
    const addOnBenefits = addOnBenefitsData?.data || [];
    const isLoading = alacarteBenefitsData.loading || modularPlansData.loading;

    const modular = useMemo(
        () => modularPlans.find((plan) => plan.id === selectedModularPlan),
        [modularPlans, selectedModularPlan]
    );
    const alacarte = useMemo(() => {
        const al: IAlacarte[] = [];
        alacarteBenefits.forEach((benefit) => {
            const defaultCount = benefit.pricingStructure === BenefitPricingStructure.PER_LIFE ? 0 : 1;
            if (benefit.subCategories.length) {
                const subs = benefit.subCategories.filter((sub) => selectedAlaCarte.includes(sub.id));
                if (subs.length) {
                    const price = subs.reduce(
                        (acc, sub) => acc + sub.price * (userBenefits[sub.id]?.length ?? defaultCount),
                        0
                    );
                    // only selected tier subcategory
                    al.push({
                        ...benefit,
                        price,
                        subCategories: subs.map((sub) => ({ ...sub, selectedUserIds: userBenefits[sub.id] }))
                    });
                }
            } else if (selectedAlaCarte.includes(benefit.id)) {
                al.push({
                    ...benefit,
                    price: benefit.price * (userBenefits[benefit.id]?.length ?? defaultCount),
                    selectedUserIds: userBenefits[benefit.id]
                });
            }
        });
        return al;
    }, [alacarteBenefits, selectedAlaCarte, userBenefits]);

    const addOns = useMemo(() => {
        const ad: IAlacarte[] = [];
        addOnBenefits.forEach((benefit) => {
            if (benefit.subCategories.length) {
                const sub = benefit.subCategories.find((sub) => selectedAddOns.includes(sub.id));
                if (sub) {
                    // only selected tier subcategory
                    ad.push({ ...benefit, price: sub.price, subCategories: [sub] });
                }
            } else if (selectedAddOns.includes(benefit.id)) {
                ad.push(benefit);
            }
        });
        return ad;
    }, [addOnBenefits, selectedAddOns]);

    const priceSummary: IPriceSummary = useMemo(() => {
        const alacartePremium = alacarte.reduce((acc, benefit) => (acc += benefit.price), 0);
        const addOnPremium = addOns.reduce((acc, benefit) => (acc += benefit.price), 0);
        const totalPremium = (modular?.price || 0) + alacartePremium + addOnPremium;
        const walletDeducted = balance ? Math.min(balance, totalPremium) : 0;
        const salaryDeducted = totalPremium - walletDeducted;
        return { totalPremium, walletDeducted, salaryDeducted, gst: 0 };
    }, [modular, alacarte, balance, addOns]);
    return { isLoading, modular, alacarte: [...alacarte, ...addOns], priceSummary };
};
export default usePremiumBreakdown;
